import React, { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import { Drawer } from '@mui/material';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './shell/shell.css';

import Icon from './components/Icon';
import ErrorBoundary from './components/ErrorBoundary';
import { isLeaving } from './auth/leaving';
import Logo from './components/Logo';
import Sidebar, { ProductTile } from './shell/Sidebar';
import { useProducts } from './product/ProductContext';
import { isOnboarding } from './product/productUtils';

import DashboardScreen from './screens/DashboardScreen';
import CreateScreen from './screens/CreateScreen';
import PlanScreen from './screens/PlanScreen';
import ReportScreen from './screens/ReportScreen';
import LinksScreen from './screens/LinksScreen';
import MemoryScreen from './screens/MemoryScreen';
import ProductProfileScreen from './screens/ProductProfileScreen';
import IcpsScreen from './screens/IcpsScreen';
import BrandVoiceScreen from './screens/BrandVoiceScreen';
import ManageProductsScreen from './screens/ManageProductsScreen';
import BillingScreen from './screens/BillingScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import SocialAccountsScreen from './screens/SocialAccountsScreen';
import PublishingScreen from './screens/PublishingScreen';
import { useBilling } from './billing/BillingContext';
import { daysUntil, formatLongDate, takePlanIntent } from './billing/format';

// The whiteboard library is large, so it loads only when the whiteboard opens
const WhiteboardScreen = lazy(() => import('./screens/WhiteboardScreen'));

// Sections with their own full-height layout instead of the padded main column
const FLUSH_SECTIONS = ['icps', 'whiteboard'];

const Welcome = ({ resumeProduct }) => {
    const navigate = useNavigate();
    return (
        <div className="ls-welcome">
            <Logo size={30} />
            <h1 className="ls-page-title" style={{ fontSize: 30 }}>
                {resumeProduct ? `Finish setting up ${resumeProduct.name}` : 'Add your first product'}
            </h1>
            <div style={{ fontSize: 16, color: 'var(--secondary)', lineHeight: 1.5 }}>
                Point us at your landing page, app store listing or docs. We'll build a product profile and
                the customer profiles every post is written for.
            </div>
            <button
                type="button"
                className="ls-btn ls-btn-primary is-large"
                onClick={() => navigate(resumeProduct ? `/products/${resumeProduct.id}/setup` : '/products/new')}
            >
                <Icon name={resumeProduct ? 'arrow_forward' : 'add'} size={18} />
                {resumeProduct ? 'Resume setup' : 'Add product'}
            </button>
        </div>
    );
};

// Payment problems and a trial about to end, on every page but billing itself
const BillingBanner = ({ section }) => {
    const navigate = useNavigate();
    const { account, pricing } = useBilling() || {};
    if (!account || section === 'billing') return null;
    if (account.paymentIssue) {
        return (
            <div className="ls-error" role="alert">
                <Icon name="credit_card_off" />
                <span style={{ flex: 1 }}>Your last payment didn't go through. Update your card to keep your weekly plan running.</span>
                <button type="button" className="ls-btn ls-btn-secondary" onClick={() => navigate('/dashboard/billing')}>Update card</button>
            </div>
        );
    }
    const trialDays = account.status === 'trialing' && !account.cancelAtPeriodEnd ? daysUntil(account.trialEnd) : null;
    if (trialDays !== null && trialDays <= 3) {
        const discount = pricing?.yearlyDiscount;
        return (
            <div className="ls-notice" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Icon name="schedule" size={18} />
                <span style={{ flex: 1 }}>
                    Your free trial ends {trialDays === 0 ? 'today' : `on ${formatLongDate(account.trialEnd)}`}. Your plan carries on automatically
                    {account.interval !== 'year' && discount ? `, or switch to yearly and save ${discount}%` : ''}.
                </span>
                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/billing')}>Review plan</button>
            </div>
        );
    }
    return null;
};

const DashboardPage = ({ user, authLoading = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentProduct, isLoading: isLoadingProducts, loadError, loadProducts } = useProducts();
    const { isLoaded: isBillingLoaded } = useBilling() || {};
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    // The page shows once the session, products and plan are all in, so banners and
    // cards that depend on them are there from the first paint instead of pushing in
    const isLoading = authLoading || (!loadError && isLoadingProducts) || isBillingLoaded === false;

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            // No authenticated user found, redirect to home page
            // Signing out lands on the landing page itself, without the sign-in modal
            if (!isLeaving()) navigate('/?showAuth=true');
            return;
        }
        // A plan picked on the landing page before signing up
        const intent = takePlanIntent();
        if (intent) navigate(`/dashboard/billing?${new URLSearchParams(intent)}`, { replace: true });
    }, [navigate, user, authLoading]);

    // Also rendered as the loading state of /auth/callback, which lands on the dashboard
    const section = (location.pathname.startsWith('/dashboard') && location.pathname.split('/')[2]) || 'dashboard';

    // Each page starts at its top
    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const isFlush = FLUSH_SECTIONS.includes(section);

    const renderContent = () => {
        if (isLoading) {
            return (
                <>
                    <div className="ls-skeleton" style={{ width: 260, height: 52 }} />
                    <div className="ls-skeleton" style={{ height: 92 }} />
                    <div className="ls-skeleton" style={{ height: 360 }} />
                </>
            );
        }
        if (loadError) {
            return (
                <div className="ls-error">
                    <Icon name="error" />
                    <span style={{ flex: 1 }}>{loadError}</span>
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={loadProducts}>Try again</button>
                </div>
            );
        }
        // Billing doesn't need a product
        // Billing and email settings don't need a product
        if (section === 'billing') return <BillingScreen />;
        if (section === 'notifications') return <ErrorBoundary key="notifications"><NotificationsScreen /></ErrorBoundary>;
        if (!currentProduct || isOnboarding(currentProduct)) {
            return <Welcome resumeProduct={currentProduct} />;
        }
        return (
            <ErrorBoundary key={`${currentProduct.id}:${section}`}>
            <Routes key={currentProduct.id}>
                <Route index element={<DashboardScreen />} />
                <Route path="social" element={<CreateScreen kind="social" />} />
                <Route path="blog" element={<CreateScreen kind="blog" />} />
                <Route path="ads" element={<CreateScreen kind="ads" />} />
                <Route path="video" element={<CreateScreen kind="video" />} />
                <Route path="plan" element={<PlanScreen />} />
                <Route path="publishing" element={<PublishingScreen />} />
                <Route path="whiteboard" element={<Suspense fallback={<div className="ls-skeleton" style={{ flex: '1 0 0', margin: 16 }} />}><WhiteboardScreen /></Suspense>} />
                <Route path="links" element={<LinksScreen />} />
                <Route path="reports" element={<ReportScreen />} />
                <Route path="profile" element={<ProductProfileScreen />} />
                <Route path="icps" element={<IcpsScreen />} />
                <Route path="icps/:icpId" element={<IcpsScreen />} />
                <Route path="voice" element={<BrandVoiceScreen />} />
                <Route path="channels" element={<SocialAccountsScreen />} />
                <Route path="memory" element={<MemoryScreen />} />
                <Route path="products" element={<ManageProductsScreen />} />
                <Route path="billing" element={<BillingScreen />} />
                <Route path="notifications" element={<NotificationsScreen />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </ErrorBoundary>
        );
    };

    return (
        <div className="ls-app">
            <Sidebar user={user} active={section} isLoading={isLoading} onSwitcherToggle={setSwitcherOpen} />

            <div className="ls-mobile-bar">
                <button
                    type="button"
                    className="ls-btn ls-btn-ghost"
                    aria-label="Open navigation"
                    onClick={() => setMobileOpen(true)}
                >
                    <Icon name="menu" size={24} />
                </button>
                {isLoading ? (
                    <div className="ls-skeleton" style={{ width: 140, height: 28 }} />
                ) : (
                    <>
                        <ProductTile product={currentProduct} size={28} radius={8} fontSize={13} />
                        <span style={{ fontSize: 15, fontWeight: 600 }}>{currentProduct?.name || 'light speed'}</span>
                    </>
                )}
            </div>
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { md: 'none' }, '& .MuiDrawer-paper': { width: 248, overflow: 'visible' } }}
            >
                <Sidebar user={user} active={section} isLoading={isLoading} onNavigate={() => setMobileOpen(false)} />
            </Drawer>

            <main className={`ls-main${isFlush && !isLoading ? ' is-flush' : ''}${switcherOpen ? ' is-dimmed' : ''}`}>
                {!isLoading && <BillingBanner section={section} />}
                {renderContent()}
            </main>
        </div>
    );
};

export default DashboardPage;
